import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPonds, fetchPondCounts } from '../api/pondsApi';
import { usePondLocation } from './usePondLocation';
export const usePondList = (options = {}) => {
    const { skipListFetch = false } = options;
    const [ponds, setPonds] = useState([]);
    const [status, setStatus] = useState('active');
    const [statusTotals, setStatusTotals] = useState({
        active: 0,
        inactive: 0,
        deleted: 0,
    });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { locationId } = usePondLocation();
    const resolvedLocationId = locationId ?? undefined;
    const abortRef = useRef(null);
    const load = useCallback(async (abortSignal) => {
        setIsLoading(true);
        setError(null);
        try {
            if (skipListFetch) {
                const counts = await fetchPondCounts(resolvedLocationId);
                if (abortSignal?.aborted)
                    return;
                setStatusTotals({
                    active: counts.active,
                    inactive: counts.inactive,
                    deleted: counts.deleted,
                });
            }
            else {
                const [data, counts] = await Promise.all([
                    fetchPonds({ page, limit, status, locationId: resolvedLocationId }),
                    fetchPondCounts(resolvedLocationId),
                ]);
                if (abortSignal?.aborted)
                    return;
                setPonds(data.ponds);
                setTotal(data.total);
                setTotalPages(data.totalPages);
                setPage(data.page);
                setLimit(data.limit);
                setStatusTotals({
                    active: counts.active,
                    inactive: counts.inactive,
                    deleted: counts.deleted,
                });
            }
        }
        catch (err) {
            if (abortSignal?.aborted)
                return;
            setError('errors.loadFailed');
        }
        finally {
            if (!abortSignal?.aborted)
                setIsLoading(false);
        }
    }, [page, limit, status, resolvedLocationId, skipListFetch]);
    const refresh = useCallback(() => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        load(controller.signal);
    }, [load]);
    useEffect(() => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        load(controller.signal);
        return () => {
            controller.abort();
        };
    }, [load]);
    const handleSetStatus = useCallback((nextStatus) => {
        setStatus(nextStatus);
        setPage(1);
    }, []);
    const handleSetLimit = useCallback((nextLimit) => {
        setLimit(nextLimit);
        setPage(1);
    }, []);
    return {
        ponds,
        status,
        statusTotals,
        page,
        limit,
        total,
        totalPages,
        isLoading,
        error,
        refresh,
        setStatus: handleSetStatus,
        setPage,
        setLimit: handleSetLimit,
    };
};
